import useWebOtp from '@/hooks/useWebOtp'
import { webOtpHost } from '@/utils/constants/config'
import faToEnNum from '@/utils/functions/convertNumbers'
import { cn } from '@/utils/helpers/classnames'
import {
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useId,
  useRef
} from 'react'

interface IOtpInputProps {
  value: string
  onChange: (value: string) => void
  inputsNumber?: number
  isError?: boolean
  errorMessage?: string
  className?: string
  inputsContainerClassName?: string
  listenKey?: string | number
}

const onlyDigits = (value: string) => faToEnNum(value).replace(/\D/g, '')

function OtpInput({
  value,
  onChange,
  inputsNumber = 4,
  isError = false,
  errorMessage,
  className,
  inputsContainerClassName,
  listenKey = 0
}: IOtpInputProps) {
  const idPrefix = useId()
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const digits = value.split('').slice(0, inputsNumber)

  const focusIndex = (index: number) => {
    const nextIndex = Math.min(Math.max(index, 0), inputsNumber - 1)
    inputRefs.current[nextIndex]?.focus()
    inputRefs.current[nextIndex]?.select()
  }

  const handleWebOtp = useCallback(
    (otp: string) => {
      onChange(otp)
      const nextIndex =
        otp.length >= inputsNumber ? inputsNumber - 1 : otp.length
      const clamped = Math.min(Math.max(nextIndex, 0), inputsNumber - 1)
      inputRefs.current[clamped]?.focus()
      inputRefs.current[clamped]?.select()
    },
    [onChange, inputsNumber]
  )

  useWebOtp({
    onSuccess: handleWebOtp,
    allowedHost: webOtpHost,
    codeLength: inputsNumber,
    listenKey
  })

  const updateValue = (nextDigits: string[]) => {
    onChange(nextDigits.join('').slice(0, inputsNumber))
  }

  const handleChange = (index: number, raw: string) => {
    const nextDigit = onlyDigits(raw).slice(-1)
    const nextDigits = Array.from(
      { length: inputsNumber },
      (_, i) => digits[i] ?? ''
    )

    nextDigits[index] = nextDigit
    updateValue(nextDigits)

    if (nextDigit && index < inputsNumber - 1) {
      focusIndex(index + 1)
    }
  }

  const handleKeyDown = (
    index: number,
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault()

      const nextDigits = Array.from(
        { length: inputsNumber },
        (_, i) => digits[i] ?? ''
      )

      if (nextDigits[index]) {
        nextDigits[index] = ''
        updateValue(nextDigits)
        return
      }

      if (index > 0) {
        nextDigits[index - 1] = ''
        updateValue(nextDigits)
        focusIndex(index - 1)
      }
      return
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusIndex(index - 1)
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusIndex(index + 1)
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasted = onlyDigits(event.clipboardData.getData('text')).slice(
      0,
      inputsNumber
    )

    if (!pasted) return

    updateValue(pasted.split(''))
    focusIndex(pasted.length >= inputsNumber ? inputsNumber - 1 : pasted.length)
  }

  return (
    <div className={cn('flex flex-col items-center', className ?? '')}>
      <div
        dir="rtl"
        className={cn(
          'my-5 flex items-center justify-center gap-3 flex-row-reverse',
          inputsContainerClassName ?? ''
        )}
      >
        {Array.from({ length: inputsNumber }, (_, index) => (
          <input
            key={`${idPrefix}-${index}`}
            ref={(node) => {
              inputRefs.current[index] = node
            }}
            id={`${idPrefix}-otp-${index}`}
            type="tel"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            autoFocus={index === 0}
            maxLength={1}
            value={digits[index] ?? ''}
            aria-invalid={isError}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onFocus={(event) => event.currentTarget.select()}
            onPaste={handlePaste}
            className={cn(
              'h-12 w-12 rounded-xl border bg-white text-center text-lg text-slate-900 outline-none transition',
              isError
                ? 'border-red-500 text-red-600 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                : 'border-slate-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-100'
            )}
          />
        ))}
      </div>

      {isError && errorMessage ? (
        <p className="mb-1 text-center text-sm text-red-500">{errorMessage}</p>
      ) : null}
    </div>
  )
}

export default OtpInput
